import {invalidateQueries, q} from "../../state";
import {
  Activity,
  UnderstandingId,
  DiagramMin,
  OwnerId,
  UnderstandingSummaryEvidenceId,
  RecordUnderstandingSummaryEvidence,
  UpdateUnderstandingSummaryEvidence,
  ProcessStep,
  ProcessStepId,
  ProcessStepContent,
  UpdateProcessStep,
} from "platformed-client/types";
import {platformedClient} from "..";

export async function get(understandingId: UnderstandingId) {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/understandings/{understanding_id}", {
    path: {understanding_id: understandingId},
  });
}

export async function listDiagrams(understandingId: UnderstandingId): Promise<DiagramMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/understandings/{understanding_id}/diagrams",
    {path: {understanding_id: understandingId}},
  );
}

export async function updateName(understandingId: UnderstandingId, name: string) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/name",
    {path: {understanding_id: understandingId}, body: name},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter(), q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function updateSummary(understandingId: UnderstandingId, summary: string | null) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/summary",
    {path: {understanding_id: understandingId}, body: summary},
  );
  await invalidateQueries([q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function updateContext(understandingId: UnderstandingId, context: string) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/context",
    {path: {understanding_id: understandingId}, body: context},
  );
  await invalidateQueries([q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function assign(understandingId: UnderstandingId, ownerId: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/owner",
    {path: {understanding_id: understandingId}, body: ownerId},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter(), q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function updateHidden(understandingId: UnderstandingId, hidden: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/hidden",
    {path: {understanding_id: understandingId}, body: hidden},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter(), q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function exportUnderstanding(understandingId: UnderstandingId) {
  return await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/export",
    {path: {understanding_id: understandingId}},
  );
}

export async function generate(understandingId: UnderstandingId) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/generate",
    {path: {understanding_id: understandingId}},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter(), q.tprm.understanding.filter(understandingId)]);
  return res;
}

export async function delete_(understandingId: UnderstandingId) {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/understandings/{understanding_id}",
    {path: {understanding_id: understandingId}},
  );
  await invalidateQueries([q.tprm.engagementUnderstandings.filter()]);
  return res;
}

export async function loadSummaryEvidence(ids: UnderstandingSummaryEvidenceId[]) {
  return await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understanding-summary-evidence/load",
    {body: ids},
  );
}

export async function listSummaryEvidence(understandingId: UnderstandingId) {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/understandings/{understanding_id}/summary-evidence",
    {path: {understanding_id: understandingId}},
  );
}

export async function addSummaryEvidence(
  understandingId: UnderstandingId,
  evidence: RecordUnderstandingSummaryEvidence,
) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/summary-evidence",
    {path: {understanding_id: understandingId}, body: evidence},
  );
  await invalidateQueries([q.tprm.understandingSummaryEvidence.filter(understandingId)]);
  return res;
}

export async function listSteps(understandingId: UnderstandingId): Promise<ProcessStep[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps",
    {path: {understanding_id: understandingId}},
  );
}

export async function listStepActivity(
  understandingId: UnderstandingId,
  processStepId: ProcessStepId,
): Promise<Activity[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps/{process_step_id}/activities",
    {path: {understanding_id: understandingId, process_step_id: processStepId}},
  );
}

export async function createStep(understandingId: UnderstandingId, step: ProcessStepContent): Promise<ProcessStepId> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps",
    {path: {understanding_id: understandingId}, body: step},
  );
  await invalidateQueries([q.tprm.understandingSteps.filter(understandingId)]);
  return res;
}

export async function updateStep(
  understandingId: UnderstandingId,
  processStepId: ProcessStepId,
  update: UpdateProcessStep,
) {
  const res = await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps/{process_step_id}",
    {path: {understanding_id: understandingId, process_step_id: processStepId}, body: update},
  );
  await invalidateQueries([
    q.tprm.understandingSteps.filter(understandingId),
    q.tprm.processStepActivity.filter(understandingId, processStepId),
  ]);
  return res;
}

export async function deleteStep(understandingId: UnderstandingId, processStepId: ProcessStepId) {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps/{process_step_id}",
    {path: {understanding_id: understandingId, process_step_id: processStepId}},
  );
  await invalidateQueries([
    q.tprm.understandingSteps.filter(understandingId),
    q.tprm.processStepActivity.filter(understandingId, processStepId),
  ]);
  return res;
}

export async function reorderStep(understandingId: UnderstandingId, processStepId: ProcessStepId, newOrdinal: number) {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/understandings/{understanding_id}/steps/{process_step_id}/reorder",
    {path: {understanding_id: understandingId, process_step_id: processStepId}, body: newOrdinal},
  );
  await invalidateQueries([q.tprm.understandingSteps.filter(understandingId)]);
  return res;
}

export async function updateSummaryEvidence(
  understandingId: UnderstandingId,
  evidenceId: UnderstandingSummaryEvidenceId,
  update: UpdateUnderstandingSummaryEvidence,
) {
  const res = await platformedClient.internalRequest(
    "patch",
    "/region/{region}/api/tprm/understandings/{understanding_id}/summary-evidence/{understanding_summary_evidence_id}",
    {path: {understanding_id: understandingId, understanding_summary_evidence_id: evidenceId}, body: update},
  );
  await invalidateQueries([
    q.tprm.understandingSummaryEvidence.filter(understandingId),
    q.tprm.summaryEvidenceById.filter(evidenceId),
  ]);
  return res;
}
